import React from "react";

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return value.$oid || value._id || value.id || value.value || '';
  return String(value);
};

const ProductDetails = ({ product, productCategories = [], stores = [], plateforms = [] }) => {
  const categoryName =
    productCategories.find(x => normalizeId(x?._id) === normalizeId(product?.category))?.name || '';
  const productImage = product?.url_product
    ? `${process.env.REACT_APP_DATA_PIC_PATH}/${String(product.url_product).replace(/^\/+/, '')}`
    : '';
  
  return (
    <div className="card p-3">
      <h5 className="card-title">Product Details</h5>
      <div className="mb-2">
        <strong>Barcode:</strong>
        {product?.barcode}
      </div>
      <div className="mb-2">
        <strong>Image:</strong>
        {productImage ? (
          <img src={productImage} width={100} alt={product?.name || 'Product'} />
        ) : (
          <span> N/A</span>
        )}
      </div>
      <div className="mb-2"><strong>Name:</strong> {product?.name}</div>
      <div className="mb-2"><strong>Regular Price:</strong> {product?.regular_price}</div>
      <div className="mb-2"><strong>WooCommerce Id:</strong> <a href={`https://www.le-celadon.ma/wp-admin/post.php?post=${product?.woocommerce_id}&action=edit`} target="_blank">{product?.woocommerce_id}</a> </div>
      <div className="mb-2"><strong>Discounted Price:</strong> {product?.discounted_price}</div>
      <div className="mb-2"><strong>Availability:</strong> {product?.availability ? "Available" : "Unavailable"}</div>
      <div className="mb-2"><strong>Category:</strong> {categoryName}</div>
      <div className="mb-2"><strong>Ref Marjane Mall:</strong> {product?.marjane?.ref || ''}</div>
          <div className="mb-2">
            <strong>Stores:</strong>
            {
              // Build a map of store availability combining product.stores and Kooul roadmap entries
              (() => {
                const map = new Map();

                // helper to normalize id
                const norm = (v) => {
                  if (v === null || v === undefined) return '';
                  if (typeof v === 'object') return v.$oid || v._id || v.id || v.value || '';
                  return String(v);
                };

                // product.stores entries take default precedence
                (Array.isArray(product?.stores) ? product.stores : []).forEach((st) => {
                  const sid = norm(st.store_id || st.storeId || st._id || st.id);
                  if (!sid) return;
                  map.set(sid, {
                    availability: st.availability,
                    stock: st.stock,
                    source: 'product'
                  });
                });

                // Kooul roadmap entries: prefer Kooul's availability when present
                (Array.isArray(product?.plateforms) ? product.plateforms : []).forEach((pf) => {
                  (Array.isArray(pf.roadmap) ? pf.roadmap : []).forEach((r) => {
                    const storeId = norm(r.store_id || (r.data && (r.data.store_id || r.data.vendorId)));
                    if (!storeId) return;

                    // determine availability from roadmap.data.is_enabled (1/0) or roadmap.data.availability.available
                    let avail;
                    if (r && r.data && typeof r.data.is_enabled !== 'undefined') {
                      avail = (r.data.is_enabled === 1 || r.data.is_enabled === true);
                    } else if (r && r.data && r.data.availability && typeof r.data.availability.available !== 'undefined') {
                      avail = !!r.data.availability.available;
                    } else if (typeof r.available !== 'undefined') {
                      avail = !!r.available;
                    }

                    const existing = map.get(storeId);
                    // Prefer Kooul roadmap availability for Kooul platform entries
                    const isKooulPf = String((pf?.type || '')).toLowerCase() === 'kooul' || (String(pf?.name || '').toLowerCase().startsWith('kooul'));
                    if (isKooulPf) {
                      map.set(storeId, {
                        availability: typeof avail === 'undefined' ? (existing ? existing.availability : undefined) : avail,
                        stock: existing ? existing.stock : undefined,
                        source: 'kooul'
                      });
                    } else if (!existing) {
                      // only set if no product store exists
                      map.set(storeId, {
                        availability: typeof avail === 'undefined' ? undefined : avail,
                        stock: existing ? existing.stock : undefined,
                        source: 'roadmap'
                      });
                    }
                  });
                });

                // Build an ordered array of entries to render: prefer product.stores order then other mapped stores
                const ordered = [];
                (Array.isArray(product?.stores) ? product.stores : []).forEach((st) => {
                  const sid = norm(st.store_id || st.storeId || st._id || st.id);
                  if (!sid) return;
                  ordered.push({ id: sid, ...map.get(sid) });
                });

                // add remaining map entries not in ordered
                for (const [id, info] of map.entries()) {
                  if (!ordered.some(o => o.id === id)) {
                    ordered.push({ id, ...info });
                  }
                }

                return ordered.map((entry, i) => {
                  const storeMeta = (Array.isArray(stores) ? stores.find(s => {
                    const sid = norm(s._id || s.value || s.id || s.rawId || s.storeCode);
                    return sid === entry.id || String(s.value) === entry.id;
                  }) : null) || null;

                  const label = storeMeta?.label || entry.id || 'Inconnu';
                  const availability = typeof entry.availability === 'undefined' ? undefined : entry.availability;
                  const badgeClass = availability === true ? 'success' : (availability === false ? 'danger' : 'secondary');
                  const availabilityLabel = availability === true ? 'Available' : (availability === false ? 'Unavailable' : 'Unknown');

                  return (
                    <div key={i} className={`badge text-bg-${badgeClass} mx-1`}>
                      <span>{label} - {availabilityLabel}</span>
                    </div>
                  );
                });
              })()
            }
          </div>
      <div className="mb-2">
        <strong>Kooul:</strong>
        {
              (Array.isArray(product?.plateforms) ? product.plateforms : [])
            .map(p => ({ entry: p, pid: normalizeId(p?.plateform_id?._id || p?.plateform_id || p?._id || p?.plateformId) }))
            .filter(({ pid }) => !!pid)
            .map(({ entry, pid }, idx) => {
              const matched = plateforms.find(pl => normalizeId(pl._id || pl.id || pl.plateform_id || pl.value) === normalizeId(pid));
              if (!matched) return null;
              const isKooul = String((matched.type || '')).toLowerCase() === 'kooul' || (matched.name || '').toLowerCase().startsWith('kooul');
              if (!isKooul) return null;
              return (
                <div key={idx} className="d-inline-block mx-1">
                  <div className="fw-bold">{matched.name || matched.type || 'Plateforme'}</div>
                  <div className="badge bg-secondary">
                    {entry.remote_id ? (Array.isArray(entry.remote_id) ? entry.remote_id.join(', ') : String(entry.remote_id)) : 'remote'}
                  </div>
                  {/* roadmap stores */}
                  {(Array.isArray(entry.roadmap) ? entry.roadmap : []).map((r, ridx) => {
                    const storeId = r?.store_id || (r?.data && (r.data.store_id || r.data.vendorId));
                    const matchingStore = stores.find(s => (
                      String(s.value) === String(storeId) || String(s.rawId) === String(storeId) || String(s.storeCode) === String(storeId)
                    ));

                    const platformType = r?.type || (r?.data && r.data.type) || entry?.type || matched?.type || 'kooul';
                    const availabilityFlag = (
                      (r && r.data && typeof r.data.availability === 'object' && (r.data.availability.available === true || r.data.availability.available === false))
                        ? r.data.availability.available
                        : (r?.available !== undefined ? r.available : (r?.data?.is_enabled === 1 ? true : (r?.data?.is_enabled === 0 ? false : undefined)))
                    );

                    const badgeClass = availabilityFlag === true ? 'success' : (availabilityFlag === false ? 'danger' : 'secondary');
                    const availabilityLabel = availabilityFlag === true ? 'Available' : (availabilityFlag === false ? 'Unavailable' : 'Unknown');

                    return (
                      <div key={ridx} className="d-inline-block mx-1">
                        <div className={`badge text-bg-${badgeClass} me-1`}>
                          {`${platformType}: ${availabilityLabel}`}
                        </div>
                        <div className="badge bg-light text-dark">{matchingStore?.label || String(storeId)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })
        }
      </div>
      <div className="mb-2">
        <strong>Stock:</strong>
        {(Array.isArray(product?.stores) ? product.stores : []).map((store, index) => (
          <div key={index} className="bg-info m-1 p-2">
            <span>
              <span className="badge text-bg-warning">{store.stock ?? 0}</span> {stores.find(s => s.value === store.store_id)?.label || "Inconnu"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductDetails;
